import axios from 'axios';
import { getFallBackAccessToken } from 'components/Common/Utils/ResourceUtils';
import {
    isLocalReportUrl,
    SHARING_AGREEMENTS_REPORT_V1_PATH,
    V1_SHARING_AGREEMENTS_REPORT_URL,
    V2_SHARING_AGREEMENTS_REPORT_URL
} from './reportEndpoints';

export const ALL_SHIPPERS_LABEL = 'All Shippers';

export const displayShippers = (shippers) => {
    const values = (shippers || []).filter((value) => value != null && String(value).trim().length > 0);
    return values.length > 0 ? values : [ALL_SHIPPERS_LABEL];
};

export const formatStringList = (values) => {
    if (!values || values.length === 0) {
        return '';
    }
    return values.filter((value) => value != null && String(value).length > 0).join('; ');
};

export const formatEventTypes = (eventTypes) => {
    if (!eventTypes || eventTypes.length === 0) {
        return '';
    }
    return eventTypes.join(', ');
};

const envelopeItems = (data) => (Array.isArray(data?.items) ? data.items : []);

export const requestErrorMessage = (reason) => {
    const data = reason?.response?.data;
    const details =
        data?.details ||
        data?.reason ||
        data?.errors?.[0]?.description ||
        data?.title;
    const status = reason?.response?.status;
    if (status && details) {
        return `${status}: ${details}`;
    }
    if (details) {
        return details;
    }
    if (status) {
        return `Request failed with status ${status}`;
    }
    return reason?.message || 'Request failed';
};

export const mergeReportResults = (v1Result, v2Result) => {
    const items = [];
    const errors = [];
    let truncated = false;

    if (v1Result.status === 'fulfilled') {
        const data = v1Result.value?.data;
        items.push(...envelopeItems(data));
        truncated = truncated || Boolean(data?.truncated);
    } else {
        errors.push({
            source: 'v1',
            message: requestErrorMessage(v1Result.reason)
        });
    }

    if (v2Result.status === 'fulfilled') {
        const data = v2Result.value?.data;
        items.push(...envelopeItems(data));
        truncated = truncated || Boolean(data?.truncated);
    } else {
        errors.push({
            source: 'v2',
            message: requestErrorMessage(v2Result.reason)
        });
    }

    return { items, truncated, errors };
};

const decodeJwtPayload = (token) => {
    if (!token || token.split('.').length < 2) {
        return null;
    }
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    return JSON.parse(atob(padded));
};

const jwtHasAccountId = (token) => {
    try {
        const claims = decodeJwtPayload(token);
        return Boolean(claims?.account_id || claims?.accountId || claims?.AccountId);
    } catch (error) {
        return false;
    }
};

const getLocalReportJwt = () => {
    let accessToken = '';
    try {
        accessToken = getFallBackAccessToken();
    } catch (error) {
        accessToken = '';
    }
    if (jwtHasAccountId(accessToken)) {
        return accessToken;
    }

    try {
        const oidcKey = Object.keys(sessionStorage).find((key) => key.startsWith('oidc.user:'));
        if (oidcKey) {
            const idToken = JSON.parse(sessionStorage.getItem(oidcKey) || '{}').id_token;
            if (jwtHasAccountId(idToken) || (!accessToken && idToken)) {
                return idToken;
            }
        }
    } catch (error) {
        // fall through to access token
    }

    return accessToken;
};

const reportParams = ({ status, carrier } = {}) => {
    const params = {};
    if (status) {
        params.status = status;
    }
    if (carrier && carrier.trim()) {
        params.carrier = carrier.trim();
    }
    return params;
};

const accountHeaders = (accountUuid) =>
    accountUuid ? { 'x-credential-account-id': accountUuid } : {};

const localReportHeaders = (url, accountUuid) => {
    const token = getLocalReportJwt();
    const credentialAccount = accountHeaders(accountUuid);
    const jwtHeader = token ? { 'x-credential-jwt': token } : {};
    const isV1 = url.includes(SHARING_AGREEMENTS_REPORT_V1_PATH);

    if (isV1 && !jwtHasAccountId(token)) {
        return { ...credentialAccount };
    }

    return { ...jwtHeader, ...credentialAccount };
};

const kongReportHeaders = (accountUuid) => {
    const token = getFallBackAccessToken();
    return {
        Authorization: `Bearer ${token}`,
        ...accountHeaders(accountUuid)
    };
};

const reportHeaders = (url, accountUuid) =>
    isLocalReportUrl(url) ? localReportHeaders(url, accountUuid) : kongReportHeaders(accountUuid);

export const fetchSharingAgreementsReport = async ({ status, carrier, accountUuid } = {}) => {
    const params = reportParams({ status, carrier });

    const [v1Result, v2Result] = await Promise.allSettled([
        axios.get(V1_SHARING_AGREEMENTS_REPORT_URL, {
            headers: reportHeaders(V1_SHARING_AGREEMENTS_REPORT_URL, accountUuid),
            params
        }),
        axios.get(V2_SHARING_AGREEMENTS_REPORT_URL, {
            headers: reportHeaders(V2_SHARING_AGREEMENTS_REPORT_URL, accountUuid),
            params
        })
    ]);

    return mergeReportResults(v1Result, v2Result);
};
